import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { config } from '../../_config/config';
import { RootState } from '../store';
import tokenService from '../../_services/token.service';
import Vacancy from '../../models/vacancy.interface';
import { decodeToken } from '../../models/decode-token';
import {getCandidateNumberByStatus} from "../../utils/get-candidate-status-number";
import {AdminCompany} from "../../models/admin-company.interface";

export interface PlanPaymentDetail {
  id: string;
  plan: string;
  plan_display: string;
  status: number;
  status_display: string;
  expiry_date: string;
  created_at: string;
  invoice_url?: string;
}

export interface BillingState {
  current_plan: PlanPaymentDetail | null;
  payment_history: PlanPaymentDetail[];
  loading: boolean;
  error: string | null;
}

export interface CompanyState {
  companyId: string;
  name: string;
  industrialSector: string;
  website: string;

  address: string;
  address_number: string;
  address_stair: string;
  address_floor: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;

  businessName: string;
  nif: string;
  description: string;
  colour: string;

  uploadedAvatar: File | null;
  email: string;

  firstName: string;
  lastName: string;
  password: string;
  phoneCode: string;
  phoneNumber: string;
  phone: string;
  logo: string;
  offers: Vacancy[];
  cover: string;
  plan?: object;
  referralCode?: string;
  promoCode: string;

  is_verified: boolean;
  terms: boolean;
  registrationError?: string | null;
  tokenExhausted: boolean;
  companyDoNotExists: boolean;
  trialPeriodEnded: boolean;
  request_counter?: {
    gpt_4_requests: number;
    counter_expires_at: string;
    total_requests: number;
  };

  companyCount: number;
  adminCompanies: AdminCompany[];
  pageTotal: number;
  billing: BillingState;
}

const initialState: CompanyState = {
  companyId: '',
  name: '',
  industrialSector: '',
  website: '',

  address: '',
  address_number: '',
  address_stair: '',
  address_floor: '',
  city: '',
  country: '',
  latitude: 0,
  longitude: 0,

  businessName: '',
  nif: '',
  description: '',
  colour: '#00BCD4',

  uploadedAvatar: null,

  email: '',
  firstName: '',
  lastName: '',
  phoneCode: '+34',
  phoneNumber: '',
  phone: '',
  offers: [],

  password: '',
  logo: '',
  cover: '',
  plan: [],
  promoCode: '',

  is_verified: false,
  terms: false,
  registrationError: '',
  tokenExhausted: false,
  companyDoNotExists: false,
  trialPeriodEnded: false,
  companyCount: 0,
  adminCompanies: [],
  pageTotal: 1,
  billing: {
    current_plan: null,
    payment_history: [],
    loading: false,
    error: null,
  },
};

export const getCompany = createAsyncThunk('company/getCompany', async () => {
  const token = tokenService.getLocalAccessToken();
  const decodedToken = decodeToken(token);

  const response = await axios.get(
    `${config.SERVER_URL}company/${decodedToken?.company_id}/`,
    {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    }
  );

  return response.data.body;
});

export const getCompanies = createAsyncThunk(
  'company/getCompanies',
  async ({
    pageNumber,
    searchValue,
    size,
    orderBy = '',
  }: {
    pageNumber: number;
    searchValue: string;
    size: number;
    orderBy?: string;
  }) => {
    const token = tokenService.getLocalAccessToken();

    const response = await axios.get(
      config.SERVER_URL + `company` +
       `/?page=${pageNumber}&size=${size}&search=${searchValue}&order_by=${orderBy}`,
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    return response.data;
  }
);

export const createCompany = createAsyncThunk(
  'company/createCompany',
  async (_, { getState }) => {
    const state: RootState = getState() as RootState;
    const companyState = state.companyReducer;
    let currentLanguage = localStorage.getItem('lang');
    if (!currentLanguage || currentLanguage == 'sp') {
      currentLanguage = 'es';
    } else if (currentLanguage == 'pt') {
      currentLanguage = 'pr';
    }

    const payload = {
      first_name: companyState.firstName,
      last_name: companyState.lastName,
      password: companyState.password,
      email: companyState.email,
      name: companyState.name,
      sector: companyState.industrialSector,
      url: companyState.website,
      address: companyState.address,
      address_number: companyState.address_number,
      address_floor: companyState.address_floor,
      address_stair: companyState.address_stair,
      city: companyState.city,
      country: companyState.country,
      latitude: companyState.latitude,
      longitude: companyState.longitude,
      business_name: companyState.businessName,
      nif: companyState.nif,
      description: companyState.description,
      phone: companyState.phoneCode + companyState.phoneNumber,
      colour: companyState.colour,
      referral_code: companyState.referralCode,
    };

    const response = await axios.post(config.SERVER_URL + 'company/?current_language=' + currentLanguage, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }
);

export const uploadCompanyLogo = createAsyncThunk(
  'company/uploadLogo',
  async (_, { getState }) => {
    const token = tokenService.getLocalAccessToken();
    const decodedToken = decodeToken(token);
    const state: RootState = getState() as RootState;

    const response = await axios.post(
      config.SERVER_URL + 'company/' + decodedToken?.company_id + '/logo/',
      { logo: state.companyReducer.uploadedAvatar },
      {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }
);

export const checkNif = createAsyncThunk(
  'company/checkNif',
  async (nif: string) => {
    try {
        const response = await axios.post(
            config.SERVER_URL + 'company/check-nif/',
            {nif: nif},
        );
        // If there's no error, the company doesn't exist yet
        return true;
    } catch (error) {
      // Company NIF exists
      return false;
    }
  }
);

export const setPlanType = createAsyncThunk(
  'company/setPlanType',
  async ({ plantype, promoCode }: { plantype: string; promoCode?: string }) => {
  const token = tokenService.getLocalAccessToken();
  let lang = localStorage.getItem('lang') || 'en';
  if (lang === 'sp') lang = 'es';

  const response = await axios.post(
    `${config.SERVER_URL}pay/plan/payment?current_language=${lang}`,
    { payment_plan: plantype, ...(promoCode ? { promo_code: promoCode } : {}) },
    {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    }
  );

  return response.data.body;
});

export const getBilling = createAsyncThunk(
  'company/getBilling',
  async () => {
    const token = tokenService.getLocalAccessToken();

    const response = await axios.get(
      `${config.SERVER_URL}pay/billing/`,
      {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    return response.data.body;
  }
);

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setName: (state, action) => {
      state.name = action.payload;
    },

    setSector: (state, action) => {
      state.industrialSector = action.payload;
    },

    setWebsite: (state, action) => {
      let website = action.payload;
      if (!website.startsWith("https://")) {
        website = "https://" + website;
      }

      state.website = website;
    },

    setCompanyAddress: (state, action) => {
      state.address = action.payload.address;
      state.address_floor = action.payload.address_floor;
      state.address_number = action.payload.address_number;
      state.address_stair = action.payload.address_stair;
      state.city = action.payload.city;
      state.country = action.payload.country;
      state.latitude = action.payload.latitude;
      state.longitude = action.payload.longitude;
    },

    setBusinessName: (state, action) => {
      state.businessName = action.payload;
    },

    setNif: (state, action) => {
      state.nif = action.payload;
    },

    setCompanyDescription: (state, action) => {
      state.description = action.payload;
    },

    setCompanyAvatar: (state, action) => {
      state.uploadedAvatar = action.payload;
    },

    setCompanyContactInfo: (state, action) => {
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.phoneCode = action.payload.phoneCode;
      state.phoneNumber = action.payload.phoneNumber;
      state.phone = action.payload.phoneCode + action.payload.phoneNumber;
    },

    setCompanyEmail: (state, action) => {
      state.email = action.payload;
    },

    setCompanyPassword: (state, action) => {
      state.password = action.payload;
    },

    setTerms: (state, action) => {
      state.terms = action.payload;
    },

    setCompanyTokenExhausted: (state, action) => {
      state.tokenExhausted = action.payload;
    },

    setCompanyExistsError: (state, action) => {
      state.companyDoNotExists = action.payload;
    },

    setTrialPeriodEnded: (state, action) => {
      state.trialPeriodEnded = action.payload;
    },

    setUpdatedCompanyData: (state, action) => {
      state.businessName = action.payload.business_name;
      state.address = action.payload.address;
      state.address_floor = action.payload.address_floor;
      state.address_number = action.payload.address_number;
      state.address_stair = action.payload.address_stair;
      state.city = action.payload.city;
      state.country = action.payload.country;
      state.latitude = action.payload.latitude;
      state.longitude = action.payload.longitude;
      state.description = action.payload.description;
      state.logo = action.payload.logo;
      state.email = action.payload.email;
      state.phone = action.payload.phone;
      state.firstName = action.payload.first_name;
      state.lastName = action.payload.last_name;
      state.nif = action.payload.nif;
      state.website = action.payload.url;
      state.name = action.payload.name;
      state.industrialSector = action.payload.sector;
      state.colour = action.payload.colour;
    },

    increaseLimitCounter: (state, action) => {
      if (!state.request_counter) return;

      state.request_counter.gpt_4_requests += action.payload;
    },

      setCompanyColour: (state, action) => {
          state.colour = action.payload;
      },

      setReferralCode: (state, action) => {
          state.referralCode = action.payload;
      },

      setPromoCode: (state, action) => {
          state.promoCode = action.payload;
      },

    resetCompanyData: () => initialState,
  },
  extraReducers(builder) {
    builder
      // Create company
      .addCase(createCompany.fulfilled, (state, _) => {
        state.registrationError = null;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.registrationError = action.error.message;
        console.error(action.error);
      })

      // Get company
      .addCase(getCompany.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.tokenExhausted = false;
        state.companyDoNotExists = false;

        state.companyId = action.payload.id;
        state.name = action.payload.name;
        state.firstName = action.payload.first_name;
        state.lastName = action.payload.last_name;
        state.phoneCode = action.payload.phone.substring(0, 3);
        state.phoneNumber = action.payload.phone.substring(3);
        state.phone = action.payload.phone;
        state.address = action.payload.address;
        state.website = action.payload.url;
        state.address_floor = action.payload.address_floor;
        state.address_number = action.payload.address_number;
        state.address_stair = action.payload.address_stair;
        state.city = action.payload.city;
        state.country = action.payload.country;
        state.nif = action.payload.nif;
        state.description = action.payload.description;
        state.industrialSector = action.payload.sector;
        state.businessName = action.payload.business_name;
        state.email = action.payload.email;
        state.logo = action.payload.logo;
        state.cover = action.payload.cover;
        state.offers = action.payload.offers;
        state.plan = action.payload.plan;
        state.request_counter = action.payload.request_counter;
        state.is_verified = action.payload.is_verified;
        state.colour = action.payload.colour;

        state.offers.map((item) => (item.checked = false));

        const trialEndsAt = new Date(action.payload.trial_ends_at);
        const today = new Date();
        // Trial period is only "ended" if trial expired AND there's no active paid plan
        const trialExpired = trialEndsAt.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0);
        const hasActivePlan = action.payload.plan != null;
        state.trialPeriodEnded = trialExpired && !hasActivePlan;
      })

      .addCase(getCompany.rejected, (state, action) => {
        if (action.error.message?.includes('403')) {
          state.tokenExhausted = true;
        }
      })

    .addCase(getCompanies.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.companyCount = action.payload.body.totalElements;
        state.adminCompanies = action.payload.body.data;
        state.pageTotal = action.payload.body.totalPages;
    })

    // Get billing
    .addCase(getBilling.pending, (state) => {
      state.billing.loading = true;
      state.billing.error = null;
    })
    .addCase(getBilling.fulfilled, (state, action) => {
      state.billing.loading = false;
      if (action.payload) {
        state.billing.current_plan = action.payload.current_plan;
        state.billing.payment_history = action.payload.payment_history;
      }
    })
    .addCase(getBilling.rejected, (state, action) => {
      state.billing.loading = false;
      state.billing.error = action.error.message || 'Failed to fetch billing';
    })
    ;
  },
});

export const {
  setName,
  setWebsite,
  setSector,
  setCompanyAddress,
  setBusinessName,
  setCompanyDescription,
  setNif,
  setCompanyAvatar,
  setCompanyContactInfo,
  setCompanyEmail,
  setCompanyPassword,
  setTerms,
  setCompanyTokenExhausted,
  setTrialPeriodEnded,
  setUpdatedCompanyData,
  setCompanyExistsError,
  resetCompanyData,
  increaseLimitCounter,
  setCompanyColour,
  setReferralCode,
  setPromoCode,
} = companySlice.actions;
export default companySlice.reducer;
